/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import dayjs, { Dayjs } from 'dayjs';
import {
  createContext,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { formatDateYYYYMMDD, isStrEmptyOrNull } from '../common/UtilityFunctions';
import useAbortController from '../common/hooks/useAbortController';
import useComments from '../common/hooks/useComments';
import useLoanPaymentBalance from '../common/hooks/useLoanPaymentBalance';
import useLoanPayoff from '../common/hooks/useLoanPayoff';
import useQidList from '../common/hooks/useQidList';
import axiosCall from '../services/axios';
import { useAuthContext } from './AuthContext';
import { useLoanContext } from './LoanContext';
import { useViewingContext } from '../contexts/ViewingContext';
import * as constants from '../common/constants';

type statusAlertType = {
  status: string;
  message: string;
};

type loanBalancesType = {
  upb: number;
  interestPaidYTD: number;
  mipYTD: number;
  escrow: number;
  escrow2: number;
  escrow3: number;
  regSuspense: number;
  otherSuspense: number;
  subSuspense: number;
  lossDrafts: number;
  unpaidLateCharges: number;
  assessments: number;
  corpAdvances: number;
  deferredInterest: number;
  deferredPrincipal: number;
};

type pullPayoffErrorsType = {
  generic: string;
};

type pullPayoffValue = {
  status: string;
  statusAlert: statusAlertType;
  setStatusAlert: (statusAlertType) => void;
  loadingDataError: boolean;
  loadingMessage: string;
  disablePullPayoff: boolean;
  validateFields: (SynetheticEvent) => void;
  errors: pullPayoffErrorsType;

  interestThroughDate: Dayjs;
  setInterestThroughDate: (Dayjs) => void;
  interestThroughDateError: boolean;
  setInterestThroughDateError: (boolean) => void;
  interestThroughDateErrorMsg: string;
  setInterestThroughDateErrorMsg: (string) => void;
  paymentAmount: number;
  setPaymentAmount: (number) => void;
  paymentAmountError: boolean;
  setPaymentAmountError: (boolean) => void;
  freezeDisbursements: boolean;
  setFreezeDisbursements: (boolean) => void;
  uploadPayoff: boolean;
  setUploadPayoff: (boolean) => void;
  externalPayoff: boolean;
  setExternalPayoff: (boolean) => void;
  addReconveyanceFee: boolean;
  setAddReconveyanceFee: (boolean) => void;
  addDeferredPrincipal: boolean;
  setAddDeferredPrincipal: (boolean) => void;
  payoffLetterFeeAgreed: boolean;
  setPayoffLetterFeeAgreed: (boolean) => void;
  payoffLetterFeeAgreedError: boolean;
  setPayoffLetterFeeAgreedError: (boolean) => void;

  pullPayoffModalOpen: boolean;
  hideReconveyanceCheckbox: boolean;

  loanBalancesInfo: loanBalancesType;
  loanConventional: boolean;
  servicerUser: boolean;
};

const PullPayoffContext = createContext<pullPayoffValue | null>(null);

const usePullPayoffContext = () => {
  const pullPayoffContext = useContext(PullPayoffContext);
  if (pullPayoffContext === undefined) {
    // throwing this error helps in development when the context is out of scope.
    throw new Error('pullPayoffContext must be inside a PullPayoffProvider');
  }
  return pullPayoffContext;
};

type pullPayoffProviderProps = {
  children: ReactNode;
};

const PullPayoffProvider = ({ children }: pullPayoffProviderProps): JSX.Element => {
  const { isServicerView } = useViewingContext();
  const startOfYearDate = formatDateYYYYMMDD(new Date(new Date().getFullYear(), 0, 1), '');
  const {
    loanNumber,
    servicerLoan,
    prepaymentPenalty,
    foreClose,
    bk,
    servicingStopCodes,
    propertyState,
    loanConventional,
    lpi,
  } = useLoanContext();
  const { servicerUser, token, firstName, lastName, userId } = useAuthContext();
  const { createComment } = useComments();
  const { loadingQid: loadingCategories, qid: categories } = useQidList('CMT4_CD.027');
  const { loadingLoanPaymentBalance, loanPaymentBalance } = useLoanPaymentBalance();
  const { loadingLoanPayoffBalance, loanPayoffBalance, pullLoanPayoff } = useLoanPayoff();
  const actionCodesQidListResponse = useQidList('CMT4_CD.025');
  const actionCodes = useMemo(() => actionCodesQidListResponse.qid, [actionCodesQidListResponse]);
  const loadingActionCodes = useMemo(() => actionCodesQidListResponse.loadingQid, [actionCodesQidListResponse]);
  const resultCodesQidListResponse = useQidList('CMT4_CD.026');
  const resultCodes = useMemo(() => resultCodesQidListResponse.qid, [resultCodesQidListResponse]);
  const loadingResultCodes = useMemo(() => resultCodesQidListResponse.loadingQid, [resultCodesQidListResponse]);
  const [loadedLoanBalances, setLoadedLoanBalances] = useState(false);
  const [errors, setErrors] = useState({ generic: '' });
  const delinquent = useMemo(() => {
    const curDate = new Date();
    const lpiDate = new Date(lpi * 1000);
    // inclusive months starting with lpi through the due date (this current month)
    const months = curDate.getMonth() - lpiDate.getMonth() + 12 * (curDate.getFullYear() - lpiDate.getFullYear());
    return months > 1;
  }, [lpi]);

  const currLoanNo = useMemo(() => {
    return isStrEmptyOrNull(servicerLoan) ? loanNumber : servicerLoan;
  }, [servicerLoan, loanNumber]);

  const [submitStatus, setSubmitStatus] = useState('done');
  const [statusAlert, setStatusAlert] = useState({
    status: '',
    message: '',
  });
  const status = useMemo(() => {
    // check statuses of loading loan data if loading
    if (
      loadingCategories ||
      loadingLoanPaymentBalance ||
      loadingLoanPayoffBalance ||
      loadingActionCodes ||
      loadingResultCodes ||
      !loadedLoanBalances
    )
      return 'loading';

    const statuses = [submitStatus];

    if (statuses.includes('error') || !!errors.generic) {
      return 'error';
    } else if (statuses.includes('loading')) {
      return 'loading';
    }

    return 'done';
  }, [
    loadingCategories,
    loadingLoanPaymentBalance,
    loadingLoanPayoffBalance,
    loadingActionCodes,
    loadingResultCodes,
    loadedLoanBalances,
    submitStatus,
    errors,
  ]);

  const [loadingMessage, setLoadingMessage] = useState('');
  const [payoffFreezeStopCode, setPayoffFreezeStopCode] = useState(constants.payoffFreezeStopCode);

  const [interestThroughDate, setInterestThroughDate] = useState(dayjs(''));
  const [interestThroughDateError, setInterestThroughDateError] = useState(false);
  const [interestThroughDateErrorMsg, setInterestThroughDateErrorMsg] = useState('');
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentAmountError, setPaymentAmountError] = useState(false);
  const [freezeDisbursements, setFreezeDisbursements] = useState(false);
  const [uploadPayoff, setUploadPayoff] = useState(true);
  const [externalPayoff, setExternalPayoff] = useState(false);
  const [addReconveyanceFee, setAddReconveyanceFee] = useState(true);
  const [addDeferredPrincipal, setAddDeferredPrincipal] = useState(true);
  const [payoffLetterFeeAgreed, setPayoffLetterFeeAgreed] = useState(false);
  const [payoffLetterFeeAgreedError, setPayoffLetterFeeAgreedError] = useState(false);

  const hideReconveyanceCheckbox = useMemo(() => {
    return propertyState === 'PR';
  }, [propertyState]);

  const pullPayoffModalOpen = useMemo(() => {
    if (!isServicerView && loanPayoffBalance?.payoffLetterFee && status === 'done' && !payoffLetterFeeAgreed) {
      return true;
    }
    return false;
  }, [isServicerView, loanPayoffBalance?.payoffLetterFee, status, payoffLetterFeeAgreed]);

  const [loanBalancesInfo, setLoanBalancesInfo] = useState({
    upb: 0,
    interestPaidYTD: 0,
    mipYTD: 0,
    escrow: 0,
    escrow2: 0,
    escrow3: 0,
    regSuspense: 0,
    otherSuspense: 0,
    subSuspense: 0,
    lossDrafts: 0,
    unpaidLateCharges: 0,
    assessments: 0,
    corpAdvances: 0,
    deferredInterest: 0,
    deferredPrincipal: 0,
  });

  const [commentData, setCommentData] = useState({
    userUid: userId,
    userName: ((firstName || '') + ' ' + (lastName || '')).trim(),
    addedByUid: userId, // 05/19/2026 - SQC-639, Justin Han-Nguyen - added this field to store the original commenter
    addedByUserName: ((firstName || '') + ' ' + (lastName || '')).trim(), // 05/19/2026 - SQC-639, Justin Han-Nguyen - added this field to store the original commenter
    actionCode: 'XX',
    actionCodeDescription: 'XX -',
    actionCodeKeyword: 'XX',
    codes: '',
    commentCategory: 'S',
    commentCategoryDescription: 'S - Customer Service Comment',
    commentCategoryKeyword: 'S',
    loanNumbers: `${loanNumber}`,
    loanNumber: '',
    text: `Payoff Letter <TotalPayoffAmount> Good thru ${interestThroughDate.format('MM/DD/YYYY')}`,
    promiseToPay: ``,
    promiseToPayAmount: '',
    resultCode: 'XX',
    resultCodeDescription: 'XX -',
    resultCodeKeyword: 'XX',
    success: '',
  });
  const [commentErrorData, setCommentErrorData] = useState({
    userUid: '',
    userName: '',
    addedByUid: '', // 05/19/2026 - SQC-639, Justin Han-Nguyen - added this field to store the original commenter
    addedByUserName: '', // 05/19/2026 - SQC-639, Justin Han-Nguyen - added this field to store the original commenter
    actionCode: '',
    actionCodeDescription: '',
    actionCodeKeyword: '',
    codes: '',
    commentCategory: '',
    commentCategoryDescription: '',
    commentCategoryKeyword: '',
    loanNumbers: '',
    loanNumber: '',
    text: '',
    promiseToPay: '',
    promiseToPayAmount: '',
    resultCode: '',
    resultCodeDescription: '',
    resultCodeKeyword: '',
    generic: '',
  });

  const { reset } = useAbortController();

  const pdfBlob = useRef(new Blob());

  const getLoanBalancesInfo = useCallback(async () => {
    try {
      if (!token || !loanNumber || loadingLoanPaymentBalance || loadedLoanBalances) return;

      setLoadingMessage('loading...');

      // initialize startOfYearMIP, in case no payment found at start of year.
      let startOfYearMIP = 0;

      // mipYTD is (current mipPaidBal) - (start of year mipPaidBal)
      // endpoint will error if there were no payments found at start of year.
      // this is fine, YTD MIP will just be current mipPaidBal.
      try {
        if (!startOfYearDate) throw Error('startOfYearDate is an empty string or null.');
        const config = reset();
        const headers = {
          'Session-Token': token,
        };
        const response = await axiosCall(
          {
            url: `live/loan/${loanNumber}/payment/balance/${startOfYearDate}`,
            method: 'GET',
          },
          config,
          headers,
        );

        startOfYearMIP = response ? response?.mipPaidBal || 0 : 0;
      } catch (error) {
        if (error.response.data.error === 'Problem obtaining balances.') {
          console.log('No MIP Paid Balance found for start of year. YTD MIP will be current Paid Balance.');
        } else {
          setStatusAlert({ status: 'error', message: 'Error loading Loan Balances' });
          console.error(`getLoanPaymentBalance.caught`, error.message, error.stack);
        }
      }

      setLoanBalancesInfo({
        upb: loanPaymentBalance?.upb || 0,
        interestPaidYTD: loanPaymentBalance?.interestPaidYTD || 0,
        mipYTD: (loanPaymentBalance?.MIPPaidBal || 0) - startOfYearMIP,
        escrow: loanPaymentBalance?.escrowRecdBal || 0,
        escrow2: loanPaymentBalance?.escrow2Bal || 0,
        escrow3: loanPaymentBalance?.escrow3Bal || 0,
        regSuspense: loanPaymentBalance?.suspenseBal || 0,
        otherSuspense: loanPaymentBalance?.otherSuspenseBal || 0,
        subSuspense: loanPaymentBalance?.subSuspenseBal || 0,
        lossDrafts: loanPaymentBalance?.lossDraftBalance || 0,
        unpaidLateCharges: loanPaymentBalance?.lcAcrdBal || 0,
        assessments: loanPaymentBalance?.assessmentBal || 0,
        corpAdvances: loanPaymentBalance?.corpAdvBal || 0,
        deferredInterest: loanPaymentBalance?.deferInterestBal || 0,
        deferredPrincipal: loanPaymentBalance?.deferPrincipalBal || 0,
      });
      setLoadedLoanBalances(true);
    } catch (error) {
      if (error.message === 'Problem obtaining balances.') {
        setStatusAlert({ status: 'error', message: 'No Payment Balances found as of today.' });
        console.error(`getLoanPaymentBalance.caught`, error.message, error.stack);
      } else {
        setStatusAlert({ status: 'error', message: 'Error loading Loan Balances' });
        console.error(`getLoanPaymentBalance.caught`, error.message, error.stack);
      }
    }
  }, [loadingLoanPaymentBalance, loadedLoanBalances, loanPaymentBalance, startOfYearDate, loanNumber, reset, token]);

  const updateComment = useCallback(
    (payoffAmount: number) => {
      const usdFormatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      });
      const totalPayoffAmount = usdFormatter.format(payoffAmount);
      let commentMsg: string = `Payoff Letter ${totalPayoffAmount} Good thru ${interestThroughDate.format(
        'MM/DD/YYYY',
      )}`;
      if (externalPayoff) {
        commentMsg = `Payoff Letter ${totalPayoffAmount} Good thru ${interestThroughDate.format(
          'MM/DD/YYYY',
        )} External Req`;
      }

      const updatedComment = {
        userUid: userId,
        userName: ((firstName || '') + ' ' + (lastName || '')).trim(),
        addedByUid: userId, // 05/19/2026 - SQC-639, Justin Han-Nguyen - added this field to store the original commenter
        addedByUserName: ((firstName || '') + ' ' + (lastName || '')).trim(), // 05/19/2026 - SQC-639, Justin Han-Nguyen - added this field to store the original commenter
        actionCode: 'XX',
        actionCodeDescription: 'XX -',
        actionCodeKeyword: 'XX',
        codes: '',
        commentCategory: 'S',
        commentCategoryDescription: 'S - Customer Service Comment',
        commentCategoryKeyword: 'S',
        loanNumbers: `${loanNumber}`,
        loanNumber: '',
        text: commentMsg,
        promiseToPay: ``,
        promiseToPayAmount: '',
        resultCode: 'XX',
        resultCodeDescription: 'XX -',
        resultCodeKeyword: 'XX',
        success: '',
      };
      setCommentData(updatedComment);
      return updatedComment;
    },
    [externalPayoff, firstName, interestThroughDate, lastName, loanNumber, userId],
  );

  const uploadPayoffLetter = useCallback(
    async (e: SyntheticEvent, payoffAmount: number) => {
      try {
        if (!loanNumber || !token) return;

        // display loading modal
        setSubmitStatus('loading');
        setLoadingMessage('Uploading Document...');

        const request = {
          url: `${process.env.REACT_APP_IMAGING_UPLOAD}`,
          method: 'POST',
          data: {
            metadata: new Blob(
              [
                JSON.stringify({
                  accessLevel: 1,
                  active: true,
                  associatedID: `${loanNumber}`,
                  borrowerAccess: true,
                  borrowerUpload: !isServicerView,
                  brokerAccess: true,
                  description: `Payoff Letter Good thru ${interestThroughDate.format('MM-DD-YYYY')}`,
                  filename: `Payoff Letter Good thru ${interestThroughDate.format('MM-DD-YYYY')}`,
                  loanNumber: `${loanNumber}`,
                }),
              ],
              {
                type: 'application/json',
              },
            ),
            image: pdfBlob.current,
          },
        };
        const config = reset();
        const headers = {
          'Session-Token': token,
          'Content-Type': 'multipart/form-data',
        };

        const response = await axiosCall(request, config, headers);
        if (!response) throw new Error(`Invalid response for loan ${currLoanNo}`);

        // Add comment on loan
        await createComment(
          e,
          updateComment(payoffAmount),
          actionCodes,
          categories,
          resultCodes,
          setCommentErrorData,
          setCommentData,
          true,
        );

        if (!isStrEmptyOrNull(commentErrorData.text)) {
          throw Error(`Error adding comment "${commentData.text}" on loan, error: ${commentErrorData.text}`);
        }

        return true;
      } catch (error) {
        console.error(`Error uploading Payoff Letter for ${loanNumber}: ${error.message}`);
        setSubmitStatus('error');
        setStatusAlert({
          status: 'error',
          message: 'Error uploading Payoff Letter.',
        });
        return false;
      }
    },
    [
      pdfBlob,
      loanNumber,
      currLoanNo,
      actionCodes,
      categories,
      commentData.text,
      commentErrorData,
      createComment,
      resultCodes,
      reset,
      token,
      isServicerView,
      interestThroughDate,
      updateComment,
    ],
  );

  const printPayoffLetter = useCallback(
    async (
      e: SyntheticEvent,
      payoffAmount: number,
      excludeDeferredPrincipal: boolean,
      excludeReconveyance: boolean,
      externalParty: boolean,
      freezeEscrow: boolean,
      paymentAmt: number,
      payoffDate: number,
      pendingInvoices: number,
    ) => {
      try {
        if (!loanNumber || !token) return;

        // display loading modal
        setSubmitStatus('loading');
        setLoadingMessage('Printing Document...');

        // Update to use new docId when Servicing Payoff Letter document is ready
        const request = {
          url: `${process.env.REACT_APP_DOCPRINTER_LOAN.replace('?', loanNumber)}`,
          responseType: 'blob' as const,
          method: 'POST',
          data: {
            docId: 'SPOFFD',
            customFields: {
              date: payoffDate,
              excludeDeferredPrincipal: excludeDeferredPrincipal,
              excludeReconveyance: excludeReconveyance,
              externalParty: externalParty,
              freezeEscrow: freezeEscrow,
              paymentAmount: paymentAmt,
              pendingInvoices: pendingInvoices,
            },
          },
        };
        const config = reset();
        const headers = {
          'Session-Token': token,
        };

        const response = await axiosCall(request, config, headers);
        if (!response) throw new Error(`Invalid response for loan ${currLoanNo}`);
        pdfBlob.current = new Blob([response], {
          type: 'application/pdf',
        });

        // Upload file before downloading. Mobile Safari opens the file in the same tab,
        // causing the function to be cut off before the file is uploaded. Users can
        // also force this to happen on other browsers.
        if (uploadPayoff) {
          const success = await uploadPayoffLetter(e, payoffAmount);
          // if failed, uploadPayoffLetter already displays error message so exit function.
          if (!success) return;
        }

        // Download file on system
        const url = window.URL.createObjectURL(pdfBlob.current);
        const a = document.createElement('a');
        a.href = url;
        a.setAttribute('download', `PayoffLetter_GoodThru_${interestThroughDate.format('MM-DD-YYYY')}_${currLoanNo}`);
        a.setAttribute('target', '_blank'); // try to open in new tab, some browsers open in same tab
        document.body.appendChild(a);
        a.click();
        a.remove();

        setTimeout(() => window.URL.revokeObjectURL(url), 100);

        // hide loading modal and show success
        setSubmitStatus('done');
        if (uploadPayoff) {
          setStatusAlert({
            status: 'success',
            message: 'Successfully printed & uploaded Payoff Letter.',
          });
        } else {
          setStatusAlert({
            status: 'success',
            message: 'Successfully printed Payoff Letter.',
          });
        }
      } catch (error) {
        console.error(`Error printing Payoff Letter for ${loanNumber}: ${error.message}`);
        setSubmitStatus('error');
        setStatusAlert({
          status: 'error',
          message: 'Error printing Payoff Letter.',
        });
      }
    },
    [loanNumber, currLoanNo, interestThroughDate, uploadPayoff, uploadPayoffLetter, reset, token],
  );

  const loadingDataError = useMemo(() => {
    if (status === 'loading') return false;
    if (
      servicingStopCodes === undefined ||
      [categories, loanPaymentBalance, loanPayoffBalance, actionCodes, resultCodes].includes(null)
    ) {
      return true;
    }
    return false;
  }, [status, servicingStopCodes, categories, loanPaymentBalance, loanPayoffBalance, actionCodes, resultCodes]);

  const disablePullPayoff = useMemo(() => {
    if (status === 'loading') return false;
    if (loadingDataError) return true;
    if (isServicerView) return false;
    let stopCodes = servicingStopCodes.map((stopCode) => stopCode.servStopCode);
    let hasStopCode = stopCodes.includes(payoffFreezeStopCode);
    if (loanBalancesInfo.deferredPrincipal !== 0) {
      return true;
    } else if (propertyState === 'NC') {
      return true;
    } else if (delinquent) {
      return true;
    } else if (prepaymentPenalty) {
      return true;
    } else if (foreClose) {
      return true;
    } else if (bk) {
      // if loan is in bankruptcy
      return true;
    } else if (hasStopCode) {
      return true;
    }
    return false;
  }, [
    status,
    loadingDataError,
    isServicerView,
    loanBalancesInfo,
    propertyState,
    prepaymentPenalty,
    foreClose,
    bk,
    servicingStopCodes,
    delinquent,
    payoffFreezeStopCode,
  ]);

  const handleSubmit = useCallback(
    async (e: SyntheticEvent) => {
      // display loading modal
      setSubmitStatus('loading');
      setLoadingMessage('Pulling Payoff...');

      try {
        // - First, update loan fields once those are implemented.
        // - Then, send request here to Loan Payoff endpoint once the endpoint is ready.
        //   - the endpoint itself will set the multi-occurrence event for External Payoffs.
        const pullPayoffResponse = await pullLoanPayoff(
          {
            externalParty: externalPayoff,
            excludeDeferredPrincipal: !addDeferredPrincipal,
            excludeReconveyance: !addReconveyanceFee,
            freezeEscrow: freezeDisbursements,
            paymentAmount: paymentAmount,
          },
          formatDateYYYYMMDD(interestThroughDate.toDate(), ''),
          setErrors,
        );

        // - if no errors, call printPayoffLetter() & uploadPayoffLetter()
        if (pullPayoffResponse.errors) throw Error(pullPayoffResponse.errors);

        await printPayoffLetter(
          e,
          pullPayoffResponse.payoff,
          pullPayoffResponse.excludeDeferredPrincipal,
          pullPayoffResponse.excludeReconveyance,
          pullPayoffResponse.externalParty,
          pullPayoffResponse.freezeEscrow,
          pullPayoffResponse.paymentAmount,
          pullPayoffResponse.payoffDate,
          pullPayoffResponse.pendingInvoices,
        );
      } catch (error) {
        console.error(`Error printing Payoff Letter for ${loanNumber}: ${error.message}`);
        setSubmitStatus('error');
        setStatusAlert({
          status: 'error',
          message: 'Error printing Payoff Letter.',
        });
      }
    },
    [
      pullLoanPayoff,
      externalPayoff,
      addDeferredPrincipal,
      addReconveyanceFee,
      freezeDisbursements,
      interestThroughDate,
      loanNumber,
      paymentAmount,
      printPayoffLetter,
    ],
  );

  const validateFields = useCallback(
    async (e: SyntheticEvent) => {
      e.preventDefault();
      let isError = false;

      if (!isServicerView && loanPayoffBalance?.payoffLetterFee && !payoffLetterFeeAgreed) {
        isError = true;
        setPayoffLetterFeeAgreedError(true);
      } else {
        setPayoffLetterFeeAgreedError(false);
      }

      if (!interestThroughDate.isValid()) {
        isError = true;
        setInterestThroughDateError(true);
        setInterestThroughDateErrorMsg('Interest Through Date is required.');
      } else {
        setInterestThroughDateError(false);
      }

      if (paymentAmount < 0) {
        isError = true;
        setPaymentAmountError(true);
      } else {
        setPaymentAmountError(false);
      }

      if (isError) {
        return;
      } else {
        // No Validation Errors, call handleSubmit()
        await handleSubmit(e);
      }
    },
    [
      isServicerView,
      loanPayoffBalance?.payoffLetterFee,
      payoffLetterFeeAgreed,
      interestThroughDate,
      paymentAmount,
      handleSubmit,
    ],
  );

  // getLoanBalancesInfo() needs to be called after loanPaymentBalance finishes loading
  useEffect(() => {
    getLoanBalancesInfo();
  }, [getLoanBalancesInfo]);

  // externalPayoff needs to be set after AuthContext finishes loading
  useEffect(() => {
    setExternalPayoff(!isServicerView);
  }, [isServicerView]);

  return (
    <PullPayoffContext.Provider
      value={{
        status,
        statusAlert,
        setStatusAlert,
        loadingDataError,
        loadingMessage,
        disablePullPayoff,
        validateFields,
        errors,

        interestThroughDate,
        setInterestThroughDate,
        interestThroughDateError,
        setInterestThroughDateError,
        interestThroughDateErrorMsg,
        setInterestThroughDateErrorMsg,
        paymentAmount,
        setPaymentAmount,
        paymentAmountError,
        setPaymentAmountError,
        freezeDisbursements,
        setFreezeDisbursements,
        uploadPayoff,
        setUploadPayoff,
        externalPayoff,
        setExternalPayoff,
        addReconveyanceFee,
        setAddReconveyanceFee,
        addDeferredPrincipal,
        setAddDeferredPrincipal,
        payoffLetterFeeAgreed,
        setPayoffLetterFeeAgreed,
        payoffLetterFeeAgreedError,
        setPayoffLetterFeeAgreedError,
        hideReconveyanceCheckbox,
        pullPayoffModalOpen,

        loanBalancesInfo,
        loanConventional,
        servicerUser,
      }}
    >
      {children}
    </PullPayoffContext.Provider>
  );
};

export { PullPayoffProvider, usePullPayoffContext };
